import {
  compact,
  filter,
  find,
  findLast,
  flatMap,
  head,
  isArray,
  isString,
  isUndefined,
  keys,
  last,
  map,
  reduce,
  reject,
  size,
} from "lodash";
import { useInjection } from "@linkfluence/core/di";
import { DateMath, PeriodRange } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { useQuery } from "@tanstack/react-query";
import { ageFromDate } from "@tootools/date";
import { GeoAPI } from "@radarly/api/geo";
import {
  ProductRepo,
  ProductRepositoryUID,
} from "@radarly/core/products/ProductRepo";
import { tr } from "@radarly/i18n";
import { Clause, InnerClause } from "@radarly/models/clause";
import { extractClauses } from "@radarly/models/clause/extractor";
import { getFieldFromInnerClause } from "@radarly/models/clause/getter";
import {
  isCustomFieldsClause,
  isDateRangeClause,
  isDocIdsClause,
  isFlagClause,
  isInnerClauseEmpty,
  isNotClause,
  isQueryClause,
  isRangeClause,
  isTermClause,
} from "@radarly/models/clause/is";
import { ClauseLabel, ClauseLabels } from "@radarly/models/clause/label";
import {
  DateRangeClause,
  RangeClause,
  TermClause,
} from "@radarly/models/clause/model";
import { FullFacet } from "@radarly/models/facets";
import { ClauseRepo, ClauseRepositoryUID } from "../repositories/ClauseRepo";

const EMPTY_LABELS: ClauseLabels[] = [];

const geoAPI = new GeoAPI();

function getLabelsFromFacet(
  facet: FullFacet,
  values: Array<string | number>,
  inclusion: boolean = true
) {
  function getTypeFromFacet() {
    if (!facet) {
      return null;
    }
    if (facet.id === Facet.Hashtags) {
      return "hashtags";
    } else if (facet.id === Facet.Mentions) {
      return "mentions";
    } else if (facet.id === Facet.Entities) {
      return "entities";
    } else {
      return null;
    }
  }
  function getFacetValue(value: string | number) {
    let facetValue = undefined;
    if (facet && isArray(facet.values) && facet.values.length > 0) {
      facetValue = find(facet.values, { id: value });
      if (isUndefined(facetValue)) {
        facetValue = find(facet.values, { id: ("" + value).toLowerCase() });
      }
      if (isUndefined(facetValue)) {
        // retry with a number
        facetValue = find(facet.values, {
          id: isString(value) ? parseInt(value) : value,
        });
      }
    }
    return facetValue;
  }

  function getLabelsFromFilterValues() {
    return reduce(
      values,
      (acc: ClauseLabel[], value) => {
        const facetValue = getFacetValue(value);
        if (!isUndefined(facetValue)) {
          acc.push({
            label: "" + facetValue.label,
            excluded: !inclusion,
            type: getTypeFromFacet(),
          });
        } else {
          acc.push({
            label: "" + value,
            type: getTypeFromFacet(),
            excluded: !inclusion,
          });
        }
        return acc;
      },
      []
    );
  }

  const labels = getLabelsFromFilterValues();
  if (facet && size(labels)) {
    return Promise.resolve({
      id: facet.id,
      labels: labels,
    });
  } else {
    return null;
  }
}

interface Dependencies {
  clauseRepo: ClauseRepo;
  productRepo: ProductRepo;
}

async function getLabelsFromTermClauseEntry(
  project: number,
  termClause: TermClause,
  facets: Record<string, FullFacet>,
  locale: Locale,
  inclusion: boolean,
  dependencies: Dependencies
): Promise<ClauseLabels> {
  const term = keys(termClause)[0] || "";
  const facet = facets[term];
  const values = termClause[term];

  const formatLabel = (label: string, inclusion: boolean) => ({
    label,
    excluded: !inclusion,
  });
  if (term === Facet.MaritalStatus) {
    return {
      id: Facet.MaritalStatus,
      labels: termClause[term].map((value) => {
        if (value === "inRelationship") {
          return {
            label: facets[term].values.find(({ id }) => id === "relationship")
              ?.label,
            excluded: false,
          };
        }
        return {
          label: facets[term].values.find(({ id }) => id === "children")?.label,
          excluded: false,
        };
      }),
    };
  }
  if (term === Facet.AuthorType) {
    return {
      id: Facet.AuthorType,
      labels: [
        formatLabel(
          tr(
            locale,
            //@ts-ignore
            "FACETS::AUTHOR::VALUES::" + (values === "me" ? "ME" : "OTHER")
          ),
          inclusion
        ),
      ],
    };
  } else if (term === Facet.Occupations) {
    const ids = values as number[];
    let occupations = [];
    occupations = await dependencies.clauseRepo.getOccupations(ids, locale);
    return {
      id: facet.id,
      labels: map(occupations, ({ label }) => formatLabel(label, inclusion)),
    };
  } else if (term === Facet.ResolvedEntities) {
    const descriptions: any = await geoAPI.post("/topicwheel/entity.json", {
      ids: values,
      locale,
    });
    return {
      id: facet.id,
      labels: map(descriptions, ({ label }) => formatLabel(label, inclusion)),
    };
  } else if (term === Facet.Regions || term === Facet.Towns) {
    const infos = await geoAPI.getGeoPlaces(values as string[], locale);
    return {
      id: facet.id,
      labels: map(infos, (place) => formatLabel(place.info.name, inclusion)),
    };
  } else if (term === Facet.ImageRecognitionLogos) {
    let labels = [];
    labels = await dependencies.clauseRepo.getBrandLogos(values);
    return {
      id: facet.id,
      labels: map(labels, ({ label }) => formatLabel(label, inclusion)),
    };
  } else if (term === Facet.ImageRecognitionPersonIdentifications) {
    let labels = [];
    labels = await dependencies.clauseRepo.getCelebrities(values);
    return {
      id: facet.id,
      labels: map(labels, ({ label }) => formatLabel(label, inclusion)),
    };
  } else if (term === Facet.Product) {
    const productValues = await dependencies.productRepo.getProducts(
      project,
      term
    );
    const labels = filter(productValues, ({ id }) => values.includes(id));
    return {
      id: facet.id,
      labels: map(labels, ({ label }) => ({ label })),
    };
  } else {
    return getLabelsFromFacet(facet, values, inclusion);
  }
}

function getLabelsFromClauseEntry(
  project: number,
  period: PeriodRange,
  innerClause: InnerClause,
  facets: Record<string, FullFacet>,
  locale: Locale,
  inclusion: boolean,
  dependencies: Dependencies
): Promise<ClauseLabels> {
  if (isInnerClauseEmpty(innerClause)) {
    return null;
  } else {
    if (isTermClause(innerClause)) {
      return getLabelsFromTermClauseEntry(
        project,
        innerClause,
        facets,
        locale,
        inclusion,
        dependencies
      );
    } else if (isNotClause(innerClause)) {
      return getLabelsFromClauseEntry(
        project,
        period,
        innerClause.not,
        facets,
        locale,
        false,
        dependencies
      );
    } else if (isCustomFieldsClause(innerClause)) {
      const term = keys(innerClause.customFields)[0] || "";
      const facet = facets[term];
      const values = innerClause.customFields[term];
      return getLabelsFromFacet(facet, values, inclusion);
    } else if (isQueryClause(innerClause)) {
      return Promise.resolve({
        id: "query",
        labels: [
          {
            label: innerClause.query,
            excluded: !inclusion,
            type: "query",
          },
        ],
      });
    } else if (isFlagClause(innerClause)) {
      const term = keys(innerClause)[0] || "";
      const facet = facets[term];
      if (!facet) return null;
      const value = find(facet.values, {
        id: innerClause[term],
      });
      return value
        ? Promise.resolve({
            id: term,
            labels: [
              {
                label: value.label,
                excluded: !value.id,
              },
            ],
          })
        : null;
    } else if (isRangeClause(innerClause)) {
      const term = getFieldFromInnerClause(innerClause);
      const facet = facets[term];
      const values = innerClause[term] as RangeClause["followers"];
      if (facet.id === Facet.Followers) {
        return Promise.resolve({
          id: term,
          labels: [
            {
              type: "followers",
              gt: values.gt,
              lt: values.lt,
              excluded: !inclusion,
            },
          ],
        });
      } else {
        if (values.lt === 0 && values.gt === 0) {
          return null;
        } else {
          const greater = find(facet.values, (value) => value.min >= values.gt);
          const lower = findLast(
            facet.values,
            (value) => value.max <= values.lt
          );
          return lower && greater
            ? Promise.resolve({
                id: term,
                labels: [
                  {
                    type: "review",
                    gt: parseInt("" + greater.id),
                    lt: parseInt("" + lower.id),
                    excluded: !inclusion,
                    lowerBound: head(facet.values).min === values.gt,
                    upperBound: last(facet.values).max === values.lt,
                  },
                ],
              })
            : null;
        }
      }
    } else if (isDateRangeClause(innerClause) && innerClause.birthDate) {
      const term = getFieldFromInnerClause(innerClause);
      const facet = facets[term];
      const values = innerClause[term] as DateRangeClause["birthDate"];
      const lowerAge = ageFromDate(values.lt);
      const upperAge = ageFromDate(values.gt);
      return Promise.resolve({
        id: term,
        labels: [
          {
            type: "age",
            gt: lowerAge,
            lt: upperAge,
            excluded: !inclusion,
            lowerBound: head(facet.values).min === lowerAge,
            upperBound: last(facet.values).max === upperAge,
          },
        ],
      });
    } else if (isDocIdsClause(innerClause)) {
      return Promise.resolve({
        id: "docIds",
        labels: [
          {
            label: innerClause.docIds.in.join(", "),
            excluded: false,
            type: "docIds",
          },
        ],
      });
    } else {
      return null;
    }
  }
}

function mergeLabels(allClauseLabels: ClauseLabels[]) {
  return reduce(
    allClauseLabels,
    (acc: ClauseLabels[], clauseLabels) => {
      const existingLabels = find(acc, { id: clauseLabels.id });
      if (existingLabels) {
        existingLabels.labels = [
          ...existingLabels.labels,
          ...clauseLabels.labels,
        ];
      } else {
        acc.push(clauseLabels);
      }
      return acc;
    },
    []
  );
}

export function getLabelsFromClause(
  project: number,
  clause: Clause,
  facets: Record<string, FullFacet>,
  locale: Locale,
  period: PeriodRange = { from: DateMath.Last_30_Days, to: DateMath.Now },
  dependencies: Dependencies
) {
  return Promise.all(
    map(extractClauses(clause), async (_clause) => {
      return Promise.all(
        map(_clause.clause.and, (innerClause) =>
          getLabelsFromClauseEntry(
            project,
            period,
            innerClause,
            facets,
            locale,
            true,
            dependencies
          )
        )
      ).then((labels) => {
        return mergeLabels(compact(reject(labels, (label) => label === null)));
      });
    })
  ).then((data) => {
    return flatMap(data);
  });
}

export function useLabelsFromClause(
  project: number,
  clause: Clause,
  facets: Record<string, FullFacet>,
  locale: Locale,
  period?: PeriodRange
) {
  const clauseRepository = useInjection<ClauseRepo>(ClauseRepositoryUID);
  const productRepository = useInjection<ProductRepo>(ProductRepositoryUID);
  const { data, isLoading } = useQuery(
    ["/clause/labels", { clause, facets, locale }],
    () => {
      return getLabelsFromClause(project, clause, facets, locale, period, {
        clauseRepo: clauseRepository,
        productRepo: productRepository,
      });
    }
  );

  return {
    loading: isLoading,
    labels: data || EMPTY_LABELS,
  };
}
